import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { ShoppingCart, Heart, Share2, Ruler, Sparkles, ChevronDown, ChevronUp, Star, User } from 'lucide-react';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [frequentlyBought, setFrequentlyBought] = useState([]);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showCareInstructions, setShowCareInstructions] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total_reviews: 0, average_rating: 0 });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: '',
    reviewer_email: '',
    rating: 5,
    title: '',
    review_text: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      
      // Fetch related products
      const relatedRes = await axios.get(`${API}/products?category=${response.data.category}&limit=4`);
      setRelatedProducts(relatedRes.data.filter(p => p.id !== id));
      
      // Fetch frequently bought together
      try {
        const fbtRes = await axios.get(`${API}/products/${id}/frequently-bought`);
        setFrequentlyBought(fbtRes.data.filter(p => p.id !== id).slice(0, 4));
      } catch (e) {
        console.log('No frequently bought together data');
      }
    } catch (error) {
      console.error('Failed to fetch product', error);
      toast.error('Product not found');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}/reviews`);
      setReviews(response.data.reviews || []);
      setReviewStats({
        total_reviews: response.data.total_reviews || 0,
        average_rating: response.data.average_rating || 0
      });
    } catch (error) {
      console.log('No reviews yet');
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.reviewer_name || !reviewForm.reviewer_email || !reviewForm.review_text) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/products/${id}/reviews`, reviewForm);
      toast.success('Thank you! Your review will be visible after approval.');
      setShowReviewForm(false);
      setReviewForm({
        reviewer_name: '',
        reviewer_email: '',
        rating: 5,
        title: '',
        review_text: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAddToCart = async () => {
    const variant = product.variants[selectedVariantIndex];
    if (variant.stock < quantity) {
      toast.error('Not enough stock available');
      return;
    }
    
    const success = await addToCart(product, selectedVariantIndex, quantity);
    if (success) {
      toast.success('Added to cart!');
    } else {
      toast.error('Failed to add to cart');
    }
  };

  const handleToggleWishlist = async () => {
    setWishlistLoading(true);
    const success = await toggleWishlist(product.id);
    if (success) {
      toast.success(isInWishlist(product.id) ? 'Removed from wishlist' : 'Added to wishlist!');
    }
    setWishlistLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!product) return null;

  const selectedVariant = product.variants[selectedVariantIndex];
  const inStock = selectedVariant?.stock > 0;

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div data-testid="product-images">
            <div className="aspect-square bg-gray-100 mb-4 overflow-hidden rounded-lg">
              <img
                src={product.images[selectedImage] || 'https://via.placeholder.com/600'}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="main-product-image"
              />
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square bg-gray-100 overflow-hidden rounded-lg border-2 ${
                      selectedImage === idx ? 'border-[#D4AF37]' : 'border-transparent'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1
              className="text-3xl sm:text-4xl font-bold mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="product-title"
            >
              {product.name}
            </h1>

            {/* Rating Display */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= Math.round(reviewStats.average_rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {reviewStats.average_rating > 0 ? reviewStats.average_rating.toFixed(1) : 'No reviews'} 
                {reviewStats.total_reviews > 0 && ` (${reviewStats.total_reviews} review${reviewStats.total_reviews !== 1 ? 's' : ''})`}
              </span>
              <button 
                onClick={() => document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm text-[#D4AF37] hover:underline"
              >
                {reviewStats.total_reviews > 0 ? 'Read reviews' : 'Write a review'}
              </button>
            </div>

            <div className="flex items-center space-x-3 mb-6">
              <span className="text-3xl font-bold" data-testid="product-price">
                ${product.base_price.toFixed(2)}
              </span>
              {product.compare_at_price && product.compare_at_price > product.base_price && (
                <span className="text-xl text-gray-500 line-through" data-testid="product-compare-price">
                  ${product.compare_at_price.toFixed(2)}
                </span>
              )}
            </div>

            <div className="prose prose-sm mb-6">
              <p className="text-gray-700" data-testid="product-description">{product.description}</p>
            </div>

            {/* Variants */}
            {product.variants.length > 0 && (
              <div className="mb-6">
                {product.variants[0].size && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Size</label>
                    <div className="flex flex-wrap gap-2">
                      {product.variants.map((variant, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedVariantIndex(idx)}
                          className={`px-4 py-2 border rounded-md text-sm font-medium transition-all ${
                            selectedVariantIndex === idx
                              ? 'border-[#D4AF37] bg-[#D4AF37] text-white'
                              : 'border-gray-300 hover:border-[#D4AF37]'
                          } ${variant.stock === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={variant.stock === 0}
                          data-testid={`variant-${idx}`}
                        >
                          {variant.size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {product.variants[0].color && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Color</label>
                    <div className="flex flex-wrap gap-2">
                      {[...new Set(product.variants.map(v => v.color))].map((color) => {
                        const variantIdx = product.variants.findIndex(v => v.color === color);
                        return (
                          <button
                            key={color}
                            onClick={() => setSelectedVariantIndex(variantIdx)}
                            className={`px-4 py-2 border rounded-md text-sm font-medium transition-all ${
                              selectedVariantIndex === variantIdx
                                ? 'border-[#D4AF37] bg-[#D4AF37] text-white'
                                : 'border-gray-300 hover:border-[#D4AF37]'
                            }`}
                            data-testid={`color-${color}`}
                          >
                            {color}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stock Status */}
            <div className="mb-6">
              {inStock ? (
                <p className="text-sm text-green-600" data-testid="stock-status">
                  In Stock ({selectedVariant.stock} available)
                </p>
              ) : (
                <p className="text-sm text-red-600" data-testid="out-of-stock">Out of Stock</p>
              )}
            </div>

            {/* Quantity */}
            {inStock && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Quantity</label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    data-testid="quantity-decrease"
                  >
                    -
                  </button>
                  <span className="text-lg font-medium w-12 text-center" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(selectedVariant.stock, quantity + 1))}
                    className="w-10 h-10 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    data-testid="quantity-increase"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Add to Cart */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={!inStock}
                className="flex-1 bg-black text-white py-3 rounded-full hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {inStock ? 'Add to Cart' : 'Out of Stock'}
              </button>
              <button
                onClick={handleToggleWishlist}
                disabled={wishlistLoading}
                className={`w-14 h-14 border-2 rounded-full flex items-center justify-center transition-all ${
                  isInWishlist(product.id) 
                    ? 'border-red-500 bg-red-50 text-red-500' 
                    : 'border-gray-300 hover:border-[#D4AF37] hover:text-[#D4AF37]'
                } ${wishlistLoading ? 'opacity-50' : ''}`}
                data-testid="wishlist-button"
                title={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                <Heart 
                  className="w-6 h-6" 
                  fill={isInWishlist(product.id) ? 'currentColor' : 'none'}
                />
              </button>
            </div>

            {/* Size Guide & Care Instructions Accordions */}
            <div className="border-t pt-6 space-y-4">
              {/* Size Guide */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowSizeGuide(!showSizeGuide)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  data-testid="size-guide-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Ruler className="w-5 h-5 text-[#D4AF37]" />
                    <span className="font-medium">Size Guide</span>
                  </div>
                  {showSizeGuide ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {showSizeGuide && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    {product.size_guide ? (
                      <p className="text-sm text-gray-600 whitespace-pre-line">{product.size_guide}</p>
                    ) : (
                      <div className="text-sm text-gray-600">
                        <p className="font-medium mb-2">General Size Guide:</p>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2">Size</th>
                              <th className="text-left py-2">Chest (in)</th>
                              <th className="text-left py-2">Waist (in)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b"><td className="py-2">XS</td><td>32-34</td><td>26-28</td></tr>
                            <tr className="border-b"><td className="py-2">S</td><td>34-36</td><td>28-30</td></tr>
                            <tr className="border-b"><td className="py-2">M</td><td>38-40</td><td>32-34</td></tr>
                            <tr className="border-b"><td className="py-2">L</td><td>42-44</td><td>36-38</td></tr>
                            <tr className="border-b"><td className="py-2">XL</td><td>46-48</td><td>40-42</td></tr>
                            <tr><td className="py-2">XXL</td><td>50-52</td><td>44-46</td></tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Care Instructions */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowCareInstructions(!showCareInstructions)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  data-testid="care-instructions-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                    <span className="font-medium">Care Instructions</span>
                  </div>
                  {showCareInstructions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {showCareInstructions && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    {product.care_instructions ? (
                      <p className="text-sm text-gray-600 whitespace-pre-line">{product.care_instructions}</p>
                    ) : (
                      <div className="text-sm text-gray-600 space-y-2">
                        <p>• Machine wash cold with like colors</p>
                        <p>• Gentle cycle recommended</p>
                        <p>• Do not bleach</p>
                        <p>• Tumble dry low or hang to dry</p>
                        <p>• Iron on low heat if needed</p>
                        <p>• Do not dry clean</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* SKU & Category */}
            {selectedVariant && (
              <div className="border-t pt-6 space-y-2 text-sm text-gray-500">
                <p><span className="font-medium text-gray-700">SKU:</span> {selectedVariant.sku}</p>
                <p><span className="font-medium text-gray-700">Category:</span> {product.category}</p>
              </div>
            )}
          </div>
        </div>

        {/* Customer Reviews Section */}
        <div id="reviews-section" className="mt-16 border-t pt-12" data-testid="reviews-section">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h2
                className="text-2xl md:text-3xl font-bold"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Customer Reviews
              </h2>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(reviewStats.average_rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-gray-600">
                  {reviewStats.average_rating > 0 ? `${reviewStats.average_rating.toFixed(1)} out of 5` : 'No reviews yet'}
                </span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">{reviewStats.total_reviews} review{reviewStats.total_reviews !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <button
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="mt-4 md:mt-0 px-6 py-2 bg-[#D4AF37] text-white rounded-full hover:bg-[#B5952F] transition-colors"
            >
              Write a Review
            </button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold mb-4">Share Your Experience</h3>
              <form onSubmit={handleReviewSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                    <input
                      type="text"
                      value={reviewForm.reviewer_name}
                      onChange={(e) => setReviewForm({...reviewForm, reviewer_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>
                    <input
                      type="email"
                      value={reviewForm.reviewer_email}
                      onChange={(e) => setReviewForm({...reviewForm, reviewer_email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rating *</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({...reviewForm, rating: star})}
                        className="p-1"
                      >
                        <Star
                          className={`w-8 h-8 cursor-pointer transition-colors ${
                            star <= reviewForm.rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300 hover:text-yellow-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Review Title</label>
                  <input
                    type="text"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({...reviewForm, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Summarize your experience"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Review *</label>
                  <textarea
                    value={reviewForm.review_text}
                    onChange={(e) => setReviewForm({...reviewForm, review_text: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Tell us about your experience with this product..."
                    required
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={submittingReview}
                    className="px-6 py-2 bg-[#D4AF37] text-white rounded-full hover:bg-[#B5952F] transition-colors disabled:opacity-50"
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReviewForm(false)}
                    className="px-6 py-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.id} className="border-b pb-6">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <div>
                          <p className="font-medium">{review.reviewer_name}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= review.rating
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            {review.verified_purchase && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                Verified Purchase
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {review.title && (
                    <h4 className="font-semibold mt-3">{review.title}</h4>
                  )}
                  <p className="text-gray-700 mt-2">{review.review_text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">No reviews yet</p>
              <p className="text-sm text-gray-500">Be the first to review this product!</p>
            </div>
          )}
        </div>

        {/* Frequently Bought Together */}
        {frequentlyBought.length > 0 && (
          <div className="mt-16 bg-[#FAFAFA] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12">
            <div className="max-w-7xl mx-auto">
              <h2
                className="text-2xl font-bold mb-6 flex items-center gap-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="frequently-bought-title"
              >
                <ShoppingCart className="w-6 h-6 text-[#D4AF37]" />
                Frequently Bought Together
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {frequentlyBought.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2
              className="text-3xl font-bold mb-8"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="related-products-title"
            >
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
